import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash, ShieldCheck, Wrench, PencilSimple, X, Check, Lock } from "@phosphor-icons/react";

const inputCls = "w-full px-3.5 py-2.5 rounded-md bg-background border border-input text-sm focus:outline-none focus:border-primary transition-colors duration-150";
const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5";

export default function Dipendenti() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("tecnico");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRole, setEditRole] = useState("tecnico");
  const [editPassword, setEditPassword] = useState("");
  const [statsOld, setStatsOld] = useState("");
  const [statsNew, setStatsNew] = useState("");
  const [statsSaving, setStatsSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role });
      toast.success("Collaboratore creato");
      setName(""); setEmail(""); setPassword(""); setRole("tecnico");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditName(u.name);
    setEditRole(u.role);
    setEditPassword("");
  };

  const saveEdit = async (u) => {
    try {
      const payload = { name: editName, role: editRole };
      if (editPassword.trim()) payload.password = editPassword.trim();
      await api.patch(`/users/${u.id}`, payload);
      toast.success("Dipendente aggiornato");
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: u.active === false ? true : false });
      toast.success(u.active === false ? "Account riattivato" : "Account disattivato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Dipendente eliminato");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeStatsPassword = async (e) => {
    e.preventDefault();
    setStatsSaving(true);
    try {
      await api.post("/stats/change-password", { old_password: statsOld, new_password: statsNew });
      toast.success("Password statistiche aggiornata");
      setStatsOld(""); setStatsNew("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setStatsSaving(false);
    }
  };

  const canManage = (u) => u.role !== "admin" || u.id === me?.id;

  return (
    <div className="p-8 space-y-8" data-testid="dipendenti-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Gestione team</p>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight leading-none">Dipendenti</h1>
        <p className="text-sm text-muted-foreground mt-2">Aggiungi, modifica o rimuovi i collaboratori. Le credenziali degli account admin sono protette.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="space-y-6 self-start">
        <form onSubmit={submit} className="border border-border rounded-md bg-card p-6 space-y-5" data-testid="employee-form">
          <h2 className="font-heading font-bold tracking-tight flex items-center gap-2">
            <UserPlus size={19} weight="duotone" /> Nuovo collaboratore
          </h2>
          <div>
            <label className={labelCls}>Nome e cognome *</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="employee-name-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="employee-email-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Password *</label>
            <input required type="text" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="employee-password-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Ruolo</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="employee-role-select" className={inputCls}>
              <option value="tecnico">Collaboratore (tecnico)</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button type="submit" disabled={saving} data-testid="employee-submit-button"
            className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
            {saving ? "Creazione..." : "Crea account"}
          </button>
        </form>

        <form onSubmit={changeStatsPassword} className="border border-border rounded-md bg-card p-6 space-y-4" data-testid="stats-password-form">
          <h2 className="font-heading font-bold tracking-tight flex items-center gap-2">
            <Lock size={18} weight="duotone" /> Password area Statistiche
          </h2>
          <p className="text-xs text-muted-foreground">La password che protegge l'accesso alle Statistiche. Per cambiarla devi prima digitare quella attuale.</p>
          <div>
            <label className={labelCls}>Password attuale *</label>
            <input required type="password" value={statsOld} onChange={(e) => setStatsOld(e.target.value)} data-testid="stats-old-password-input" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nuova password *</label>
            <input required type="password" minLength={4} value={statsNew} onChange={(e) => setStatsNew(e.target.value)} data-testid="stats-new-password-input" className={inputCls} />
          </div>
          <button type="submit" disabled={statsSaving} data-testid="stats-password-change-button"
            className="w-full py-3 rounded-md bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 disabled:opacity-50 transition-opacity duration-150">
            {statsSaving ? "Salvataggio..." : "Cambia password"}
          </button>
        </form>
        </div>

        <div className="xl:col-span-2 border border-border rounded-md bg-card overflow-hidden self-start">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Nome</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Email</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Ruolo</th>
                <th className="px-5 py-3 text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Stato</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-accent/40 transition-colors duration-150" data-testid={`employee-row-${u.id}`}>
                  <td className="px-5 py-3.5 font-semibold">
                    {editingId === u.id ? (
                      <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`edit-name-${u.id}`} className={inputCls} />
                    ) : u.name}
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">{u.email}</td>
                  <td className="px-5 py-3.5">
                    {editingId === u.id ? (
                      <select value={editRole} onChange={(e) => setEditRole(e.target.value)} data-testid={`edit-role-${u.id}`} className={inputCls}>
                        <option value="tecnico">Collaboratore</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                        {u.role === "admin" ? <ShieldCheck size={14} className="text-primary" /> : <Wrench size={14} className="text-muted-foreground" />}
                        {u.role === "admin" ? "admin" : "collaboratore"}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-bold uppercase ${u.active === false ? "text-red-500" : "text-green-500"}`}
                      data-testid={`employee-status-${u.id}`}>
                      {u.active === false ? "Disattivato" : "Attivo"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right space-x-2 whitespace-nowrap">
                    {editingId === u.id ? (
                      <>
                        <input value={editPassword} onChange={(e) => setEditPassword(e.target.value)} placeholder="Nuova password (opzionale)"
                          data-testid={`edit-password-${u.id}`} className={`${inputCls} inline-block w-44 mr-2`} />
                        <button onClick={() => saveEdit(u)} data-testid={`save-edit-${u.id}`}
                          className="text-green-500 hover:opacity-80 transition-opacity duration-150 align-middle">
                          <Check size={17} weight="bold" />
                        </button>
                        <button onClick={() => setEditingId(null)} data-testid={`cancel-edit-${u.id}`}
                          className="text-muted-foreground hover:text-foreground transition-colors duration-150 align-middle">
                          <X size={17} weight="bold" />
                        </button>
                      </>
                    ) : (
                      <>
                        {canManage(u) && (
                          <button onClick={() => startEdit(u)} data-testid={`edit-employee-${u.id}`}
                            className="text-muted-foreground hover:text-foreground transition-colors duration-150 align-middle">
                            <PencilSimple size={15} />
                          </button>
                        )}
                        {u.id !== me?.id && canManage(u) && (
                          <>
                            <button onClick={() => toggleActive(u)} data-testid={`toggle-employee-${u.id}`}
                              className="text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors duration-150">
                              {u.active === false ? "Riattiva" : "Disattiva"}
                            </button>
                            <button onClick={() => remove(u)} data-testid={`delete-employee-${u.id}`}
                              className="text-muted-foreground hover:text-destructive transition-colors duration-150 align-middle">
                              <Trash size={15} />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
